<script setup lang="ts">
  import {
    useApiRequestsAbort,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import { useCommonStore, useQuoteStore, useUserStore } from '@/shared/store'
  import {
    SELECT_PLAN_INSURANCE_VALUES_EUR_KEY,
    SELECT_PLAN_INSURANCE_VALUES_REG_KEY,
    SELECT_PLAN_INSURANCE_VALUES_WW_KEY,
    SELECT_PLAN_INSURANCE_VALUES_WWEXCL_KEY,
    SELECT_PLAN_INSURANCE_VALUES_WW_WWEXCL_KEY,
  } from '@/shared/constants'
  import { computed, defineAsyncComponent, onBeforeMount, ref } from 'vue'
  import { useAbTest } from '@/shared/composables/useAbTest'
  import { OutboundFlowRoutes } from '@/shared/types/router.types'
  import {
    type Country,
    CountryBoundType,
    FileCategory,
    type Plan,
    PromoCodeTypes,
    SchemeType,
    WordingDocName,
  } from '@/shared/types/api.types'
  import { useRouter } from 'vue-router'
  import { LOAD_EVENT_NAME } from '@/widgets/Flow/FlowSelectPlanWidget/FlowSelectPlanWidget.constants'
  import { useI18n } from 'vue-i18n'
  import { TravelDirection } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'
  import { useFeaturesStore } from '@/shared/store/featuresStore/featuresStore'

  const emit = defineEmits<Emits>()

  const FlowSelectPlanWidgetInsuranceValues = defineAsyncComponent(() =>
    import('@/widgets/Flow').then((m) => m.FlowSelectPlanWidgetInsuranceValues)
  )

  const FlowSelectPlanWidgetV3 = defineAsyncComponent(() =>
    import('@/widgets/Flow/FlowSelectPlanWidget').then((m) => m.FlowSelectPlanWidgetV3)
  )

  interface Emits {
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  useApiRequestsAbort()
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const quoteStore = useQuoteStore()
  const featuresStore = useFeaturesStore()

  const { getAbTest } = useAbTest()
  const { push } = useRouter()
  const { getFormData } = useFormDataWithRedirect()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const gaService = useGoogleAnalytics()

  const { locale } = useI18n()

  const plans = ref<Plan[]>([])
  const isInsuranceValuesSelectPlan = ref(false)
  const policyWording = ref('')
  const listOfDestionations = ref<Country[]>([])
  const destinationOptions = ref<Country[]>([])

  const sendShowPlansAnalytics = () => {
    if (!plans.value || plans.value.length === 0) return

    const showPlansEventParams = plans.value.map((plan) => ({
      item_name: plan.name,
      price: plan.price,
      schemecode: plan.schemecode,
    }))

    gaService.sendOutboundSelectPlanShowPlans(showPlansEventParams)
  }

  const fetchQatarWordings = () =>
    commonStore
      .getQatarWordings(FileCategory.TRAVEL, WordingDocName.POLICY_WORDING)
      .then((data) => {
        policyWording.value = data[0]?.Link || ''
      })

  const fetchCountries = () =>
    userStore.fetchCountries(CountryBoundType.OUTBUND, locale.value).then((data) => {
      listOfDestionations.value = data
      destinationOptions.value = listOfDestionations.value
    })

  const getQuote = async (): Promise<void> => {
    const destinationIds = userStore.destination.map((country) => country?.id)
    if (!destinationIds.length) {
      await push({ name: OutboundFlowRoutes.DESTINATION })
      return
    }
    let type: PromoCodeTypes | undefined
    if (userStore.isAffinity && userStore.promocode) {
      type = PromoCodeTypes.AFFINITY
    }
    const quote = await quoteStore.getTravelQuoteUae(
      userStore.promocode ?? '',
      0,
      destinationIds,
      type
    )
    if (quote.schemes.length === 0) {
      return Promise.reject('quote.schemes.length === 0')
    } else {
      plans.value = quote.schemes
      quoteStore.setProductBonus(quote.productBonus, true)
      quoteStore.setCoinsDiscountPrice(quote.coinsDiscountPrice, true)
      quoteStore.setQuoteWithCoinsDiscount(quote.quoteWithCoinsDiscount, true)
      sendShowPlansAnalytics()
    }
  }

  const abTestData = computed(() => {
    const hasType = (type: SchemeType) => plans.value.some((plan) => plan.schemeTypeCode === type)

    const isWorldwide = hasType(SchemeType.Worldwide)
    const isWorldwideExcl = hasType(SchemeType.WorldwideExcl)
    const isRegional = hasType(SchemeType.Regional)
    const isFlyEurope = hasType(SchemeType.FlyEurope)

    if (isWorldwideExcl) {
      if (isWorldwide) {
        return {
          key: SELECT_PLAN_INSURANCE_VALUES_WW_WWEXCL_KEY,
          group: ['TRAVELQATINSVAL-WW_WWEXCL-B1', 'TRAVELQATINSVAL-WW_WWEXCL-B2'],
        }
      }
      if (isRegional) {
        return {
          key: SELECT_PLAN_INSURANCE_VALUES_REG_KEY,
          group: ['TRAVELQATINSVAL-WWEXCL_REG-B1', 'TRAVELQATINSVAL-WWEXCL_REG-B2'],
        }
      }
      if (isFlyEurope) {
        return {
          key: SELECT_PLAN_INSURANCE_VALUES_EUR_KEY,
          group: ['TRAVELQATINSVAL-WWEXCL_EUR-B1', 'TRAVELQATINSVAL-WWEXCL_EUR-B2'],
        }
      }

      return {
        key: SELECT_PLAN_INSURANCE_VALUES_WWEXCL_KEY,
        group: ['TRAVELQATINSVAL-WWEXCL-B1', 'TRAVELQATINSVAL-WWEXCL-B2'],
      }
    }

    return {
      key: SELECT_PLAN_INSURANCE_VALUES_WW_KEY,
      group: ['TRAVELQATINSVAL-WW-B1', 'TRAVELQATINSVAL-WW-B2'],
    }
  })

  onBeforeMount(async () => {
    emit(LOAD_EVENT_NAME, async () => {
      if (!(await getFormData(TravelDirection.OUTBOUND, false))) return
      await Promise.all([getQuote(), fetchQatarWordings(), fetchCountries()])

      if (featuresStore.isSelectPlanInsuranceValuesEnabled) {
        const { key, group } = abTestData.value
        await getAbTest(key)
        isInsuranceValuesSelectPlan.value = group.includes(commonStore.getAbValue(key))
      }

      onPerformanceCheckEnd()
      gaService.sendViewPage(TravelDirection.OUTBOUND, AnalyticsPageName.SELECT_PLAN)
    })
  })
</script>

<template>
  <FlowSelectPlanWidgetV3
    v-if="isInsuranceValuesSelectPlan"
    :plans="plans"
    :policy-wording="policyWording"
    :list-of-destionations="listOfDestionations"
  />
  <div v-else>
    <FlowSelectPlanWidgetInsuranceValues
      :plans="plans"
      :policy-wording="policyWording"
      :list-of-destionations="listOfDestionations"
      v-bind="$attrs"
    />
  </div>
</template>
